import { useEffect, useRef, useState } from "react";

/**
 * VoiceInput — browser-native Web Speech API mic button.
 * Streams recognized transcript to onTranscript(text) as final phrases arrive.
 * Interim results shown to the user via onInterim(text) for real-time feedback.
 *
 * Not supported in Firefox (Web Speech API is Chrome/Edge/Safari only).
 * When unsupported, the button is hidden and a small note is optionally shown.
 */
export default function VoiceInput({ onTranscript, onInterim, disabled, size = "md" }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    rec.onresult = (event) => {
      let interim = "";
      let finalChunk = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) finalChunk += r[0].transcript;
        else interim += r[0].transcript;
      }
      if (finalChunk.trim()) onTranscript?.(finalChunk.trim());
      if (interim && onInterim) onInterim(interim);
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        setPermissionDenied(true);
      }
      setListening(false);
    };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    return () => {
      try {
        rec.stop();
      } catch (err) {
        if (typeof console !== "undefined") console.debug("[VoiceInput] cleanup stop() ignored:", err?.message);
      }
    };
  }, [onTranscript, onInterim]);

  function toggle() {
    const rec = recognitionRef.current;
    if (!rec || disabled) return;
    if (listening) {
      try {
        rec.stop();
      } catch (err) {
        console.debug("[VoiceInput] stop() ignored:", err?.message);
      }
      setListening(false);
      return;
    }
    try {
      rec.start();
      setListening(true);
      setPermissionDenied(false);
    } catch (err) {
      // SpeechRecognition.start() throws if already running — expected in some browsers.
      console.debug("[VoiceInput] start() ignored:", err?.message);
    }
  }

  if (!supported) return null;

  const sizeCls =
    size === "sm"
      ? "w-9 h-9"
      : size === "lg"
        ? "w-14 h-14"
        : "w-11 h-11";

  return (
    <div className="inline-flex items-center gap-2" data-testid="voice-input-wrap">
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-label={listening ? "Stop voice input" : "Start voice input"}
        data-testid="voice-input-button"
        className={`${sizeCls} inline-flex items-center justify-center rounded-full border transition-colors ${
          listening
            ? "bg-red-50 border-red-300 text-red-600 animate-pulse"
            : "bg-white border-slate-200 text-slate-500 hover:border-slate-400 hover:text-slate-900"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        {listening ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="22" />
          </svg>
        )}
      </button>
      {listening && (
        <span className="mono-caps text-red-600 animate-pulse" data-testid="voice-listening">
          Listening…
        </span>
      )}
      {permissionDenied && (
        <span className="text-xs text-red-600" data-testid="voice-permission-denied">
          Microphone blocked. Allow it in your browser settings.
        </span>
      )}
    </div>
  );
}
